<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { t } from "@/plugins/i18n";
import { useBaseStore } from "@/stores/base";
import useDialog from "@/ui/composables/useDialog";
import PricelistSyncDialog from "@/modules/booking_experts/pages/Components/Modals/PricelistSyncDialog.vue";
import { toastApiResult } from "@/toaster";
import { formatNumber } from "chartjs-graph";
import { useRouter } from "vue-router";
import PricelistTypesModal from "@/modules/booking_experts/pages/Components/Modals/PricelistTypesModal.vue";
import CopyPricelistCurvesModal from "@/modules/booking_experts/pages/Components/Modals/CopyPricelistCurvesModal.vue";
import { usePriceListImportStore } from "@/stores/priceListImportStore";

const store = useBaseStore();
const priceListImports = usePriceListImportStore();

// Types
type MasterPriceDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceDO;
type MasterPriceRow = MasterPriceDO & {
	is_managed: boolean;
	administration_name: string;
};

interface TableHeader {
	title: string;
	key: string;
	align?: "start" | "end";
	sortable?: boolean;
}

// Constants
const ADMINISTRATION_HEADER: TableHeader = {
	title: t("pricelist.index.table.headers.administration"),
	key: "administration_name",
};
const BASE_TABLE_HEADERS: TableHeader[] = [
	{ title: t("pricelist.index.table.headers.name"), key: "name" },
	{ title: t("pricelist.index.table.headers.types"), key: "rentable_type_ids" },
	{
		title: t("pricelist.index.table.headers.historic_complex_prices_7d_count"),
		key: "historic_complex_prices_7d_count",
	},
	{
		title: t("pricelist.index.table.headers.capacity"),
		key: "capacity_max",
		align: "start",
	},
	{
		title: t("pricelist.index.table.headers.actions"),
		key: "action",
		align: "end",
		sortable: false,
	},
] as const;

// State
const loading = ref(false);
// Track which item is currently being processed for managed conversion
const managedLoadingItemId = ref<number | null>(null);
const query = ref("");

// The administration column is only meaningful once a search spans multiple
// administrations; with only one administration it would just repeat the
// switcher above the table.
const tableHeaders = computed<TableHeader[]>(() => {
	const isSearching = query.value.trim().length > 0;

	if (!isSearching || store.bexAdministrations.length <= 1) {
		return BASE_TABLE_HEADERS;
	}

	return [BASE_TABLE_HEADERS[0], ADMINISTRATION_HEADER, ...BASE_TABLE_HEADERS.slice(1)];
});
const api = useApiClient("bookingExperts");
const priceslists = ref<MasterPriceDO[]>([]);
const showSyncDialog = ref(false);
const selectedPricelistId = ref<string | null>(null);
const selectedRentableTypeIds = ref<number[]>([]);
const showPricelistTypesModal = ref<boolean>(false);
const showCopyPricelistCurvesModal = ref(false);
const pricelistToCopyId = ref<number | null>(null);
const dialog = useDialog();
const selected = ref([]);
const router = useRouter();
const batchActions = computed(() => {
	return [
		{ title: "Batch assign", permission: "can_use_batch_curve_assignment" },
	]
		.filter((item) =>
			useBaseStore().currentUser.availablePermissions.includes(item.permission)
		)
		.map((item) => item.title);
});
const showSelect = computed(() => batchActions.value.length > 0);

// API Functions
async function fetchMasterPrices(): Promise<void> {
	loading.value = true;
	const result = await api.getMasterPrices();

	if (result.isOk()) {
		priceslists.value = result.value;
	}
	loading.value = false;
}

const refreshedImports = new Set<number>();
watch(
	() => priceListImports.progressByPriceListId,
	(progressByPriceListId) => {
		Object.values(progressByPriceListId).forEach((progress) => {
			const priceListId = progress.master_price_list_id;

			if (priceListImports.isImporting(priceListId)) {
				refreshedImports.delete(priceListId);
				return;
			}

			if (refreshedImports.has(priceListId)) {
				return;
			}

			refreshedImports.add(priceListId);
			fetchMasterPrices();
		});
	},
	{ deep: true }
);

/**
 * Imports always belong to the source price list; a managed list has no prices of
 * its own to import, its prices come from its source's import.
 */
const importsPriceListId = (priceList: MasterPriceDO): number =>
	priceList.managing_price_list_id ?? priceList.id;

const openImports = (priceList: MasterPriceDO) => {
	router.push({
		name: "pricing.imports",
		params: { id: importsPriceListId(priceList) },
	});
};

function openSyncDialog() {
	showSyncDialog.value = true;
}

function openCopyPricelistCurvesModal(id: number) {
	pricelistToCopyId.value = id;
	showCopyPricelistCurvesModal.value = true;
}

function getRentableTypeIds(
	item: MasterPriceDO,
	managedOnly: boolean = false
): number[] {
	if (managedOnly && item.is_source) {
		return (
			managedPriceLists.value[item.id.toString()]
				?.rentable_type_ids_with_cluster ?? []
		);
	}

	let allTypes = item.rentable_type_ids_with_cluster ?? [];
	if (item.is_source) {
		allTypes = [
			...allTypes,
			...(managedPriceLists.value[item.id.toString()]
				?.rentable_type_ids_with_cluster ?? []),
		];
	}

	return allTypes;
}

function getAmountOfTypes(item: MasterPriceDO) {
	return getRentableTypeIds(item).length;
}

function onSyncCompleted() {
	// Refresh the main table after sync is completed
	fetchMasterPrices();
}

async function convertToManaged(id: number) {
	const choice = await dialog.confirm({
		title: "Convert to managed pricelist",
		message:
			"Converting this unmanaged pricelist, will make it managed and start importing the pricelists.",
	});

	if (choice) {
		try {
			managedLoadingItemId.value = id;
			await toastApiResult(api.convertToManaged(id));
			await fetchMasterPrices();
		} catch (error) {
			console.error("Error converting to managed pricelist:", error);
		} finally {
			managedLoadingItemId.value = null;
		}
	}
}

function openPricelistTypes(pricelistName: string, rentableTypeIds: number[]) {
	selectedPricelistId.value = pricelistName;
	selectedRentableTypeIds.value = rentableTypeIds;
	showPricelistTypesModal.value = true;
}

function batchActionSelected(action) {
	if (action === "Batch assign") {
		router.push({
			name: "pricing.apply-batch",
			query: {
				ids: selected.value,
			},
		});
	}
}

// Computed properties
const canCopyCurves = computed(
	() =>
		useBaseStore().currentUser?.availablePermissions?.includes(
			"can_use_copy_curves"
		) ?? false
);

const isPriceListImporting = computed(() => {
	return (id: number) => {
		// Once progress is broadcast it is the most accurate signal; until then fall
		// back to the persisted timestamp.
		const progress = priceListImports.progressByPriceListId[id.toString()];

		return progress
			? priceListImports.isImporting(id)
			: priceListsById.value[id.toString()]?.import_finished_at === null;
	};
});

const priceListsById = computed<Record<string, MasterPriceDO>>(() => {
	return priceslists.value.reduce((acc, item) => {
		acc[item.id.toString()] = item;
		return acc;
	}, {});
});

const managedPriceLists = computed<Record<string, MasterPriceDO>>(() => {
	return priceslists.value.reduce((acc, item) => {
		if (item.managing_price_list_id) {
			acc[item.managing_price_list_id] = item;
		}
		return acc;
	}, {});
});

const administrationNameById = computed<Record<number, string>>(() => {
	return store.bexAdministrations.reduce((acc, administration) => {
		acc[administration.id] = administration.name;
		return acc;
	}, {} as Record<number, string>);
});

const priceListsForTable = computed<MasterPriceRow[]>(() => {
	const isSearching = query.value.trim().length > 0;

	return priceslists.value
		.filter((item) => item.is_source)
		.filter(
			(item) =>
				isSearching ||
				item.administration_id === store.selectedBexAdministration?.id
		)
		.map((item) => {
			return {
				...item,
				is_managed: managedPriceLists.value[item.id.toString()] !== undefined,
				historic_complex_prices_7d_count:
					managedPriceLists.value[item.id.toString()]
						?.historic_complex_prices_7d_count ?? 0,
				administration_name:
					administrationNameById.value[item.administration_id ?? -1] ??
					`#${item.administration_id}`,
			};
		})
		.sort((a, b) => {
			if (a.is_managed !== b.is_managed) {
				return a.is_managed ? -1 : 1;
			}

			return a.name.localeCompare(b.name);
		});
});

// Lifecycle
onMounted(fetchMasterPrices);
</script>

<template>
	<copy-pricelist-curves-modal
		v-model:is-open="showCopyPricelistCurvesModal"
		:selected-id="pricelistToCopyId"
		@refresh="fetchMasterPrices"
	/>

	<v-card class="mt-5 pt-5 px-5 pb-3">
		<div class="d-flex flex-row ga-3">
			<v-text-field
				v-model="query"
				label="Search"
				prepend-inner-icon="mdi-magnify"
				hide-details
				single-line
			/>
			<v-btn
				height="48"
				@click="openSyncDialog"
				:disabled="loading"
				variant="flat"
				color="secondary"
			>
				<v-icon icon="fa-light fa-sync" class="mr-2" />
				{{ t("pricelist.sync.button") }}
			</v-btn>
		</div>
		<v-data-table
			:headers="tableHeaders"
			:items="priceListsForTable"
			:loading="loading"
			:search="query"
			:show-select="showSelect"
			:item-value="(item) => item.id"
			v-model="selected"
		>
			<template v-slot:item.name="{ item }">
				<div class="d-flex align-center name font-weight-medium w-100">
					<div
						v-tooltip="
							t(
								`pricelist.index.table.tooltips.${
									managedPriceLists[item.id.toString()]
										? 'managed'
										: 'unmanaged'
								}`
							)
						"
						class="chit d-inline"
						:style="{
							backgroundColor: managedPriceLists[item.id.toString()]
								? '#6ec957'
								: '#E66161',
						}"
					/>
					{{ item.name }}
				</div>
			</template>
			<template v-slot:item.rentable_type_ids="{ item }">
				<div class="d-flex align-center">
					<div>
						{{ getAmountOfTypes(item) }}
					</div>
					<div>
						<v-btn
							v-if="getAmountOfTypes(item) > 0"
							size="30"
							variant="text"
							v-tooltip="
								t('pricelist.curves.table.actions.showUsedRentableTypes')
							"
							@click="
								openPricelistTypes(
									item.name,
									getRentableTypeIds(item, item.is_managed)
								)
							"
						>
							<i class="fa-solid fa-circle-info"></i>
						</v-btn>
					</div>
				</div>
			</template>
			<template v-slot:item.historic_complex_prices_7d_count="{ item }">
				{{
					item.is_managed
						? formatNumber(item.historic_complex_prices_7d_count)
						: "-"
				}}
			</template>
			<template v-slot:item.capacity_max="{ item }">
				{{
					item.capacity_max +
					(managedPriceLists[item.id.toString()]?.capacity_max ?? 0)
				}}
			</template>
			<template v-slot:item.action="{ item }">
				<div class="d-flex align-center justify-end flex-nowrap">
					<v-btn
						v-if="canCopyCurves && item.curve_count"
						v-tooltip="'Copy curves'"
						:loading="isPriceListImporting(item.id)"
						size="30"
						@click="openCopyPricelistCurvesModal(item.id)"
						color="primary"
						variant="text"
					>
						<v-icon size="16" icon="fa-light fa-clone" />
					</v-btn>
					<v-btn
						v-tooltip="t('pricelist.index.table.tooltips.import-prices')"
						size="30"
						@click="openImports(item)"
						color="primary"
						variant="text"
					>
						<v-icon size="16" icon="fa-light fa-rotate" />
					</v-btn>
					<v-btn
						v-tooltip="t('pricelist.index.table.tooltips.view')"
						size="30"
						:to="`/pricing/apply/${item.id}`"
						color="primary"
						variant="text"
						data-testid="view-price-model-applications"
						:data-is-managed="item.is_managed"
					>
						<v-icon size="16" icon="fa-light fa-calendars" />
					</v-btn>
					<v-btn
						v-if="!item.is_managed"
						v-tooltip="t('pricelist.index.table.tooltips.convert')"
						size="30"
						color="success"
						variant="tonal"
						:loading="managedLoadingItemId === item.id"
						@click="convertToManaged(item.id)"
					>
						<v-icon size="16" icon="fa-light fa-play" />
					</v-btn>
					<v-btn
						v-if="managedPriceLists[item.id.toString()]"
						v-tooltip="t('pricelist.index.table.tooltips.history')"
						size="30"
						:to="`/pricing/history/${item.id}`"
						color="primary"
						variant="text"
					>
						<v-icon size="16" icon="fa-light fa-timeline-arrow" />
					</v-btn>
				</div>
			</template>
			<template v-slot:footer.prepend>
				<div
					style="width: 200px"
					v-if="selected.length > 0"
					class="align-center"
				>
					<v-select
						:items="batchActions"
						placeholder="Actions"
						class="w-100"
						density="compact"
						hide-details
						@update:modelValue="batchActionSelected"
					/>
				</div>
				<v-spacer />
			</template>
		</v-data-table>
	</v-card>

	<!-- Sync Dialog -->
	<PricelistSyncDialog
		v-model="showSyncDialog"
		@sync-completed="onSyncCompleted"
	/>
	<PricelistTypesModal
		v-if="showPricelistTypesModal"
		v-model="showPricelistTypesModal"
		:pricelist-name="selectedPricelistId"
		:key="selectedPricelistId"
		:rentable-type-ids="selectedRentableTypeIds"
	/>
</template>

<style scoped>
.chit {
	min-width: 10px;
	min-height: 10px;
	width: 10px;
	height: 10px;
	border-radius: 50%;
}
</style>
